import React, { useState, useEffect } from "react";
import ExerciseCard from "../components/ExerciseCard.js";
import { catalogText } from "../assets/content.js";
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import toast from "react-hot-toast";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebaseConfig";
import SearchIcon from "@mui/icons-material/Search";
import SEOHead from "../components/SEOHead";
import { BASE_URL } from "../config";

// Exercises with animated GIF cards (generated via generate-videos.mjs)
const exercisesWithGif = [
  "shoulderPress", "squat", "pushUp", "deadBug", "bridge", "lunge", "legRaise",
  "pullUp", "lateralExternalRotation", "muscleUp",
  "pilatesHundred", "toeTouch", "standingObliqueCrunch",
  "plank", "treePose", "pushUpGame", "squatGame"
];

// Build image paths for lazy loading — uses dynamic import so images are
// only fetched when the card scrolls into view (via loading="lazy" on <img>)
const getExerciseImagePath = (key) =>
  exercisesWithGif.includes(key)
    ? require(`../assets/exercise-cards/${key}.gif`)
    : require(`../assets/exercise-cards/${key}.png`);

// Category mapping for exercises
const exerciseCategories = {
  squat: "Legs",
  pushUp: "Upper Body",
  deadBug: "Core",
  bridge: "Core",
  pullUp: "Upper Body",
  lateralExternalRotation: "Upper Body",
  muscleUp: "Upper Body",
  plank: "Core",
  pilatesHundred: "Core",
  lunge: "Legs",
  legRaise: "Core",
  toeTouch: "Flexibility",
  standingObliqueCrunch: "Core",
  treePose: "Balance",
  shoulderPress: "Upper Body",
  pushUpGame: "Games",
  squatGame: "Games",
};

function Catalog() {
  const [searchTerm, setSearchTerm] = useState("");
  const [pinnedExercises, setPinnedExercises] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");

  // Get unique categories
  const categories = ["All", ...new Set(Object.values(exerciseCategories))];

  const filteredExercises = Object.keys(catalogText)
    .filter((exerciseKey) =>
      exerciseKey.toLowerCase().includes(searchTerm.toLowerCase().replace(/\s/g, ""))
    )
    .filter((exerciseKey) => {
      if (activeFilter === "All") return true;
      return exerciseCategories[exerciseKey] === activeFilter;
    })
    .sort((a, b) => {
      const aPinned = pinnedExercises.includes(a);
      const bPinned = pinnedExercises.includes(b);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      return 0;
    });

  const togglePin = async (exerciseKey) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      toast.error("You must be logged in to pin an exercise.");
      logEvent(analytics, "notification_received");
      return;
    }

    const docRef = doc(db, "users", user.email, "pinnedExercises", "pinnedExercises");

    try {
      const docSnap = await getDoc(docRef);
      let updatedPins = [];

      if (!docSnap.exists()) {
        updatedPins = [exerciseKey];
        await setDoc(docRef, { pinnedExercises: updatedPins });
      } else {
        const currentPins = docSnap.get("pinnedExercises") || [];
        const isAlreadyPinned = currentPins.includes(exerciseKey);

        updatedPins = isAlreadyPinned
          ? currentPins.filter((key) => key !== exerciseKey)
          : [...currentPins, exerciseKey];

        await updateDoc(docRef, {
          pinnedExercises: updatedPins,
        });
      }

      setPinnedExercises(updatedPins);
    } catch (e) {
      console.error("Failed to toggle pin:", e);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const docSnap = await getDoc(
            doc(db, "users", user.email, "pinnedExercises", "pinnedExercises")
          );
          if (docSnap.exists()) {
            setPinnedExercises(docSnap.get("pinnedExercises"));
          }
        } catch (e) {
          console.error("Error reading document:", e);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="catalog-page">
      <SEOHead
        title="WeGuide Movement Analysis | AI-Powered Exercise Assessment"
        description="Digital movement assessment with real-time feedback. Analyse squats, push-ups, planks and 15+ exercises using your webcam. No downloads needed."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "WeGuide Movement Analysis",
          applicationCategory: "HealthApplication",
          operatingSystem: "Web browser",
          description: "Digital movement assessment for clinical trials and exercise guidance using browser-based pose estimation.",
          url: BASE_URL,
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
        }}
      />
      {/* Header */}
      <div className="catalog-header">
        <h1 className="catalog-title">Exercise Catalog</h1>
        <p className="catalog-subtitle">
          Discover and practice exercises with real-time AI-powered form analysis.
        </p>
      </div>

      {/* Toolbar: Tabs + Search */}
      <div className="catalog-toolbar">
        <div className="catalog-tabs">
          {categories.map((category) => (
            <button
              key={category}
              className={`catalog-tab ${activeFilter === category ? "catalog-tab-active" : ""}`}
              onClick={() => setActiveFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="catalog-search-wrapper">
          <SearchIcon className="catalog-search-icon" />
          <input
            type="text"
            placeholder="Search exercises..."
            className="input input-bordered catalog-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Exercise Grid */}
      {filteredExercises.length > 0 ? (
        <div className="catalog-grid">
          {filteredExercises.map((exerciseKey) => (
            <ExerciseCard
              key={exerciseKey}
              title={exerciseKey
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
              description={catalogText[exerciseKey]}
              link={
                exerciseKey === "pushUpGame"
                  ? "/pushUpGame"
                  : exerciseKey === "squatGame"
                  ? "/squatGame"
                  : `/exercise?exercise=${exerciseKey}`
              }
              image={getExerciseImagePath(exerciseKey)}
              isPinned={pinnedExercises.includes(exerciseKey)}
              onPinToggle={() => togglePin(exerciseKey)}
              category={exerciseCategories[exerciseKey]}
            />
          ))}
        </div>
      ) : (
        <div className="catalog-empty">
          <p className="catalog-empty-text">
            No exercises match your search criteria.
          </p>
        </div>
      )}
    </div>
  );
}

export default Catalog;
